import { useState } from 'react';
import { GitBranch, Pause, Play, Plus } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import {
  useJourneyCatalogue,
  useJourneys,
  useSaveJourney,
  useSegments,
  useSetJourneyStatus,
} from './api';
import { STEP_LABELS } from './labels';
import type { Journey, JourneyStep } from './types';

export function JourneysPage() {
  const { data: journeys = [], isLoading } = useJourneys();
  const [editing, setEditing] = useState<Journey | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Journeys"
        description="Triggered sequences. Every send goes through the same suppression, consent and frequency checks a campaign does — an automated sequence is exactly where those get forgotten."
        actions={
          <Can permission="marketing.campaign.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New journey
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <TableSkeleton columns={3} />
      ) : journeys.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <GitBranch className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No journeys yet. A journey is a sequence that runs itself: wait, send, tag, branch.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {journeys.map((journey) => (
            <JourneyCard key={journey.id} journey={journey} onEdit={() => setEditing(journey)} />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <JourneyDialog
          journey={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function JourneyCard({ journey, onEdit }: { journey: Journey; onEdit: () => void }) {
  const setStatus = useSetJourneyStatus(journey.id);
  const [error, setError] = useState<string | null>(null);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-medium">{journey.name}</h3>
              <Badge
                variant={
                  journey.status === 'ACTIVE'
                    ? 'success'
                    : journey.status === 'PAUSED'
                      ? 'warning'
                      : 'outline'
                }
              >
                {journey.status.toLowerCase()}
              </Badge>
            </div>
            {journey.description && (
              <p className="mt-0.5 text-sm text-muted-foreground">{journey.description}</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              {journey.triggerType.replace(/_/g, ' ')}
              {journey.segment && ` · ${journey.segment.name}`}
              {` · ${journey.stepCount} steps`}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex gap-4 text-end text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Enrolled</div>
                <div className="tabular font-semibold">{journey.enrolled}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Active</div>
                <div className="tabular font-semibold">{journey.active}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Finished</div>
                <div className="tabular font-semibold">{journey.completed}</div>
              </div>
            </div>

            <Can permission="marketing.campaign.update">
              <div className="flex gap-2">
                {journey.status === 'ACTIVE' ? (
                  <Button size="sm" variant="outline" onClick={() => setStatus.mutate('PAUSED')}>
                    <Pause className="h-4 w-4" />
                    Pause
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setError(null);
                      try {
                        await setStatus.mutateAsync('ACTIVE');
                      } catch (caught) {
                        setError(
                          caught instanceof ApiRequestError
                            ? caught.message
                            : 'Could not start it.',
                        );
                      }
                    }}
                  >
                    <Play className="h-4 w-4" />
                    Start
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onEdit}>
                  Edit
                </Button>
              </div>
            </Can>
          </div>
        </div>

        {journey.steps.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t pt-3">
            {journey.steps.map((step, index) => (
              <span key={index} className="flex items-center gap-1.5">
                <span className="rounded-md bg-muted px-2 py-1 text-[11px]">
                  {STEP_LABELS[step.type] ?? step.type}
                  {step.type === 'wait' && ` ${step.days ?? 0}d`}
                </span>
                {index < journey.steps.length - 1 && (
                  <span className="text-muted-foreground">→</span>
                )}
              </span>
            ))}
          </div>
        )}

        <FormError message={error} />
      </CardContent>
    </Card>
  );
}

function JourneyDialog({ journey, onClose }: { journey?: Journey; onClose: () => void }) {
  const save = useSaveJourney();
  const { data: catalogue } = useJourneyCatalogue();
  const { data: segments = [] } = useSegments();
  const { data: users = [] } = useUserOptions();

  const [name, setName] = useState(journey?.name ?? '');
  const [description, setDescription] = useState(journey?.description ?? '');
  const [triggerType, setTriggerType] = useState(journey?.triggerType ?? 'segment_entry');
  const [segmentId, setSegmentId] = useState(journey?.segment?.id ?? '');
  const [steps, setSteps] = useState<JourneyStep[]>(
    journey?.steps ?? [{ type: 'wait', days: 1 }],
  );
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<JourneyStep>) => {
    const next = [...steps];
    next[index] = { ...next[index], ...patch };
    setSteps(next);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{journey ? `Edit ${journey.name}` : 'New journey'}</DialogTitle>
          <DialogDescription>
            Steps run in order. Each enrolment is advanced one step at a time and what it did is
            written down, so a journey can be explained after the fact.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pe-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Starts when</Label>
              <Select
                value={triggerType}
                onChange={(event) => setTriggerType(event.target.value)}
              >
                {(catalogue?.triggers ?? []).map((trigger) => (
                  <option key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </option>
                ))}
              </Select>
            </div>
            {triggerType === 'segment_entry' && (
              <div className="space-y-1.5">
                <Label>Which segment</Label>
                <Select value={segmentId} onChange={(event) => setSegmentId(event.target.value)}>
                  <option value="">Choose one</option>
                  {segments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Label>Steps</Label>
            {steps.map((step, index) => (
              <div key={index} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <span className="tabular flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-medium">
                    {index + 1}
                  </span>
                  <Select
                    className="h-8 w-auto flex-1 text-sm"
                    value={step.type}
                    onChange={(event) => update(index, { type: event.target.value })}
                  >
                    {(catalogue?.stepTypes ?? []).map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                  {steps.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSteps(steps.filter((_, i) => i !== index))}
                    >
                      ×
                    </Button>
                  )}
                </div>

                {step.type === 'wait' && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Days</Label>
                      <Input
                        className="h-8"
                        type="number"
                        min={0}
                        value={step.days ?? 0}
                        onChange={(event) => update(index, { days: Number(event.target.value) })}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Hours</Label>
                      <Input
                        className="h-8"
                        type="number"
                        min={0}
                        value={step.hours ?? 0}
                        onChange={(event) => update(index, { hours: Number(event.target.value) })}
                      />
                    </div>
                  </div>
                )}

                {step.type === 'send_email' && (
                  <div className="space-y-2">
                    <Input
                      className="h-8"
                      placeholder="Subject"
                      value={step.subject ?? ''}
                      onChange={(event) => update(index, { subject: event.target.value })}
                    />
                    <Textarea
                      rows={4}
                      className="font-mono text-xs"
                      placeholder="<p>Hello {{first_name|there}},</p>"
                      value={step.html ?? ''}
                      onChange={(event) => update(index, { html: event.target.value })}
                    />
                  </div>
                )}

                {(step.type === 'add_tag' || step.type === 'remove_tag') && (
                  <Input
                    className="h-8"
                    placeholder="Tag name"
                    value={step.tag ?? ''}
                    onChange={(event) => update(index, { tag: event.target.value })}
                  />
                )}

                {step.type === 'condition' && (
                  <div className="space-y-2">
                    <Input
                      className="h-8"
                      placeholder="Has this tag…"
                      value={step.check ?? ''}
                      onChange={(event) => update(index, { check: event.target.value })}
                    />
                    <div className="space-y-1">
                      <Label className="text-xs">If not, skip forward by</Label>
                      <Input
                        className="h-8"
                        type="number"
                        min={0}
                        value={step.thenSkip ?? 0}
                        onChange={(event) =>
                          update(index, { thenSkip: Number(event.target.value) })
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      One condition is supported — whether the contact carries a tag. Anything
                      richer needs a visual builder, and a half-built condition engine is worse
                      than an honest single check.
                    </p>
                  </div>
                )}

                {(step.type === 'create_task' || step.type === 'notify_user') && (
                  <div className="space-y-2">
                    <Select
                      className="h-8 text-sm"
                      value={step.assigneeId ?? ''}
                      onChange={(event) => update(index, { assigneeId: event.target.value })}
                    >
                      <option value="">Choose a colleague</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </Select>
                    <Input
                      className="h-8"
                      placeholder="Title"
                      value={step.title ?? ''}
                      onChange={(event) => update(index, { title: event.target.value })}
                    />
                  </div>
                )}
              </div>
            ))}

            <Button
              size="sm"
              variant="outline"
              onClick={() => setSteps([...steps, { type: 'wait', days: 1 }])}
            >
              Add a step
            </Button>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || name.trim().length < 2 || steps.length === 0}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  id: journey?.id,
                  name,
                  description: description || undefined,
                  triggerType,
                  segmentId: segmentId || undefined,
                  steps,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                );
              }
            }}
          >
            Save journey
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
